import { Qualify } from "./pallet/Qualify"
import { Section } from "./pallet/Section"
import { AddImages } from "./AddImages"
import { PalletComments } from "./pallet/PalletComments"
import { DefaultMap, ImageTemp, ImageType, InitialPallets, NewGrower, StepSection } from "../interfaces/intakes.reports"
import { AccordionCard } from './cards/AccordionCard';
import { useEffect, useState } from "react"
import { Steps } from "./pallet/Steps"
import toast from "react-hot-toast"
import { useFetchImagesSection, useFetchPalletSection, useFinishPallet, useRemovePallet } from "../api/useReportTemp"
import { useQueryClient } from "@tanstack/react-query"
import { ModalConfirmation } from "./ui/ModalConfirmation"
import { Gallery } from "./Gallery"
import { useDeleteImage } from "../api/useDeleteImage"
import { IoCaretForwardCircleSharp, IoCheckmarkCircle } from "react-icons/io5"
import { ModalSplit } from "./ui/ModalSplit"
import { SplitButton } from "./pallet/SplitButton"
import PopUpButton from "./modals/PopUpButton"
import useOnlineStatus from "../hooks/useOnlineStatus"
import { DetailName, SectionName } from "../interfaces/interfaces"
import { PalletReferenceName } from "./ui/PalletReferenceName"
import { PiEyedropperSample } from "react-icons/pi";
import { PalletOrSampleNumber } from "./ui/PalletOrSampleNumber"


interface Props {
    reportId: string
    index: number
    setPallets: React.Dispatch<React.SetStateAction<InitialPallets[]>>
    pallet: InitialPallets
    pallets: InitialPallets[]
    isLastPallet: boolean
    splitPallet: (palletNumber: number, palletId: string, split: NewGrower, palletIndex: number) => Promise<void>
    addDefectComment: () => void
    applyToAll: (section: SectionName, label: string, name: string, value: string | number | boolean | string[]) => void
    addNewPallet: (isSample: boolean) => void
    defaults: DefaultMap
    isLoading?: boolean
    isAddingPallet?: boolean
}

interface PalletTitleProps {
    isDisabled?: boolean
}

export const Pallets = ({ setPallets, pallet, reportId, splitPallet, index, isLastPallet, pallets, addDefectComment, applyToAll, addNewPallet, defaults, isLoading: isApplying = false, isAddingPallet = false }: Props) => {

    const sectionSteps: StepSection[] = [
        { detailName: "labels", sectionStep: 1 },
        { detailName: "appearance", sectionStep: 2 },
        { detailName: "pallgrow", sectionStep: 3 },
    ]

    const [step, setStep] = useState<number>(1)

    const { mutateAsync, isLoading } = useFetchPalletSection()
    const { mutateAsync: mutateImages, isLoading: isLoadingImages } = useFetchImagesSection()
    const { mutateAsync: mutateFinish, isLoading: isLoadingFinish } = useFinishPallet()
    const { mutate: mutateRemovePallet, isLoading: isRemoving } = useRemovePallet()
    const { mutateAsync: mutateDeleteImage } = useDeleteImage()

    const isOnline = useOnlineStatus();

    const [accordion, setAccordion] = useState<boolean>(false)
    const [openSplit, setOpenSplit] = useState<boolean>(false)
    const [confirmation, setConfirmation] = useState<boolean>(false)

    // const [hasTwins, setHasTwins] = useState<boolean>(false)

    const queryClient = useQueryClient()

    useEffect(() => {
        pallet.complete === null && setStep(0)
        setPallets(pallet => pallet.map(pall => {
            return {
                ...pall,
                labels: { ...pall.labels, imagesTemp: [] },
                appearance: { ...pall.appearance, imagesTemp: [] },
                pallgrow: { ...pall.pallgrow, imagesTemp: [] },
            }
        }))

        pallet.complete && setAccordion(false)
    }, [pallet.complete, setPallets])


    // useEffect(() => {
    //     const hasTwins = pallets.filter(pall => pall.pallet === pallet.pallet).length > 1
    //     setHasTwins(hasTwins)
    // }, [pallets, pallet.pallet])

    const saveAndNext = async (detailName: DetailName, images: ImageTemp[]) => {

        const { data, minor, major } = pallet[detailName]!

        if (!isOnline) return toast.error("You are offline, please check your connection")
        if (!data) return

        const basicData = {
            reportId,
            palletId: pallet.pid,
            detailName,
        }

        const fetchData = mutateAsync({ ...basicData, palletData: data, palletMajor: major, palletMinor: minor })
        const fetchImages = mutateImages({ ...basicData, imagesTemp: images })

        await Promise.all([fetchData, fetchImages])
            .then(([data, images]) => {

                const updatedPallets = pallets.map(pallet => {
                    if (pallet.pid === data.pallet.pid) {
                        return images
                            ? {
                                ...pallet,
                                [detailName]: {
                                    ...data.pallet[detailName],
                                    images: [...data.pallet[detailName].images, ...images.images]
                                }
                            }
                            // : data.pallet
                            : {
                                ...pallet,
                                [detailName]: data.pallet[detailName],
                            }
                    } return pallet
                })

                setPallets(updatedPallets)

                queryClient.setQueryData(['reportTemp', reportId], (old: any) => {
                    return {
                        ...old,
                        singleReport: {
                            ...old.singleReport,
                            pallets: updatedPallets
                        }
                    }
                })
                setStep(step => step + 1)
                addDefectComment()

            })
            .catch((error) => console.log(error))
    }

    const finishPallet = async () => {

        if (!isOnline) return toast.error("You are offline, please check your connection")

        await mutateFinish({
            reportId,
            palletId: pallet.pid,
            score: pallet.score,
            action: pallet.action,
            grade: pallet.grade,
            comments: pallet.comments || ""
        }, {
            onSuccess: async (data) => {

                const updatedPallets = pallets.map(pallet => {
                    if (pallet.pid === data.pallet.pid) {
                        return data.pallet
                    } return pallet
                })

                setPallets(updatedPallets)

                queryClient.setQueryData(['reportTemp', reportId], (old: any) => {
                    return {
                        ...old,
                        singleReport: {
                            ...old.singleReport,
                            pallets: updatedPallets
                        }
                    }
                })
                setAccordion(false)
                setStep(1)

            }
        })

    }

    const removePallet = () => {
        mutateRemovePallet({ palletId: pallet.pid, reportId }, {
            onSuccess: () => setPallets(pallets => {
                const updatedPallets = pallets.filter(pall => pall.pid !== pallet.pid)
                if(updatedPallets.length === 1){
                    delete updatedPallets[0].pallet_sample
                }
                return updatedPallets
            })
        })
    };

    const deletePalletImage = async (image: ImageType) => {
        await mutateDeleteImage({
            keyName: image.key,
            keyLow: image.key_low,
            reportId,
            palletId: pallet.pid
        }, {
            onSuccess: () => {
                setPallets(pallets => pallets.map(pall => {
                    if (pall.pid === pallet.pid) {
                        return {
                            ...pall,
                            images: pallet.images.filter(img => img.key !== image.key)
                        }
                    } return pall
                }))
            }
        })
    };

    const startPallet = () => { setStep(step => step + 1); setAccordion(true) }
    const removeConfirmation = () => setConfirmation(true)

    const PopBtn = () => {
        return (
            <PopUpButton>
                {step === 0 && <button className='py-02' onClick={() => startPallet()} >Start Pallet</button>}
                {/* {(pallet.splitPallet === null || !hasTwins) && <p className='py-02' onClick={() => setOpenSplit(true)}>Split Pallet</p>} */}
                {/* {(pallet.splitPallet === null || !hasTwins) && <p className='py-02' onClick={() => setOpenSplit(true)}>Split Pallet</p>} */}
                {/* {(pallet.splitPallet && !hasTwins) && <p className='py-02' onClick={() => cleanSplitData()}>Remove Split Data</p>} */}
                <button className={`${isLastPallet ? "text-red" : "tw-text-neutral-300"} py-02`} disabled={!isLastPallet} onClick={() => removeConfirmation()} >Remove Pallet</button>
            </PopUpButton>
        )
    };

    const PalletTitle = ({ isDisabled }: PalletTitleProps) => {
        return (
            <div className='flex'>
                <div className='flex'>
                    <PalletOrSampleNumber pallet={pallet} isDisabled={isDisabled} />
                    {
                        pallets.length === 1 &&
                        <button
                        className="tw-ml-4 tw-aspect-square tw-w-7 tw-bg-blue-200 tw-rounded-md tw-flex tw-items-center tw-justify-center disabled:tw-opacity-50"
                        disabled={isAddingPallet}
                        onClick={() => addNewPallet(true)}
                        >
                            <PiEyedropperSample size={20} className="tw-text-blue-900" />
                        </button>
                    }
                </div>
                {pallet.complete &&
                    <IoCheckmarkCircle color="#5ac85d" size={22} className="ml-05" />
                }
                <SplitButton pallet={pallet} reportId={reportId} />
            </div>
        )
    };

    return (
        <>
            {
                confirmation &&
                <ModalConfirmation
                    closeConfirmation={() => setConfirmation(false)}
                    buttonName="Remove Pallet"
                    msg="Are you sure you want to remove this pallet?"
                    action={() => removePallet()}
                    isLoading={isRemoving}
                />
            }

            {
                openSplit &&
                <ModalSplit
                    closeModal={() => setOpenSplit(false)}
                    splitPallet={splitPallet}
                    reportId={reportId}
                    pallet={pallet}
                    palletIndex={index}
                />
            }

            {
                step === 0
                    ?
                    <div className={`mb-2 card-pallet deactive-pallet`}>

                        <div className='flex-space-between'>
                            <PalletTitle isDisabled />
                            <PalletReferenceName pallet={pallet} reportId={reportId} />
                            <div className="flex flex-gap position-relative">
                                <PopBtn />
                                <button
                                    className='accordion-open-btn blue'
                                    onClick={() => startPallet()}>
                                    <IoCaretForwardCircleSharp color="#fff" />
                                </button>
                            </div>
                        </div>
                    </div>
                    :
                    <AccordionCard
                        defaultOpen={accordion}
                        setAccordion={setAccordion}
                        additionalBtn={<PopBtn />}
                        centerBtn={<PalletReferenceName pallet={pallet} reportId={reportId} />}
                        fullWidth
                        className="mb-1"
                        title={<PalletTitle />}
                    >

                        {
                            sectionSteps.map(section =>
                                (step === section.sectionStep && pallet[section.detailName]) &&
                                <Section
                                    key={section.detailName}
                                    reportId={reportId}
                                    pallet={pallet}
                                    setPallets={setPallets}
                                    detailName={section.detailName}
                                    saveAndNext={saveAndNext}
                                    step={step}
                                    setStep={setStep}
                                    isLoading={isLoading || isLoadingImages}
                                    applyToAll={applyToAll}
                                    defaults={defaults}
                                    isApplying={isApplying}
                                />
                            )
                        }

                        {
                            step === 4 &&

                            <div className={`pallet-step ${step === 4 ? "active-step" : "pt-2 pb-2 border-b"}`}>
                                <Steps
                                    step={step}
                                    setStep={setStep}
                                    actionNext={finishPallet}
                                    isLoading={isLoadingFinish}
                                />
                                <p className='bold font-medium mb-05 text-green'>Score</p>

                                <Qualify setPallets={setPallets} pallet={pallet} applyToAll={applyToAll} defaults={defaults} isApplying={isApplying} />
                                <PalletComments setPallets={setPallets} pallet={pallet} />

                                {
                                    pallet.images.length > 0 &&
                                    <Gallery images={pallet.images} deleteImage={deletePalletImage} fullwidth reportId={reportId} />
                                }

                                <AddImages
                                    reportId={reportId}
                                    pallet={pallet}
                                    setPallets={setPallets}
                                />
                            </div>
                        }
                    </AccordionCard>
            }
        </>
    )
}

